import { Alert } from '@/components/ui/alert';
import { useUnfinishedPayoutsSummaryQuery } from '@/hooks/queries/use-unfinished-payouts-summary-query';
import { formatLongDate } from '@/lib/format-long-date';
import { formatCents } from '@/libs/utils/format-money';
import { UNFINISHED_PAYOUTS_HREF } from '@/pages/payments/payouts/payouts-tab-url';
import { Button, Text } from '@chakra-ui/react';
import type { UnfinishedPayoutsSummary } from '@fluenceur/types';
import { InfoCircle } from 'iconoir-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

/** The summary carries an amount in currency units; the shared formatter reads cents. */
const formatChargeAmount = (summary: UnfinishedPayoutsSummary, language: string): string =>
  summary.currency
    ? formatCents(Math.round(summary.totalChargeAmount * 100), summary.currency, language)
    : String(summary.totalChargeAmount);

/**
 * Puts the payouts a brand started and never paid back in front of it.
 *
 * Their commissions are held by the unpaid payout, so they are missing from the
 * list to pay right below: without this notice the brand sees fewer
 * commissions than it owes and no reason why. The notice only points to where
 * the payment can be finished; it never makes a held commission selectable.
 *
 * The deadline sentence waits for the reminder: a payout is only ever cancelled
 * at a date the brand was told, so there is nothing to announce before that.
 */
const UnfinishedPayoutsNotice: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { data: summary } = useUnfinishedPayoutsSummaryQuery();

  if (!summary || summary.count === 0) return null;

  const language = i18n.language;
  // The reminder email dates the deadline in UTC: the notice names that same day.
  const deadline = formatLongDate(summary.earliestDeadlineAt, language, { timeZone: 'UTC' });

  return (
    <Alert
      w="full"
      status="info"
      icon={<InfoCircle />}
      alignItems={{ base: 'flex-start', md: 'center' }}
      flexWrap="wrap"
      columnGap={3}
      rowGap={3}
      title={t('commissions.unfinishedPayouts.title', { count: summary.count })}
      endElement={
        // Solid, like the dashboard's other alert actions: an outline button fills
        // grey on hover, which reads as disabled on the tinted alert.
        <Button asChild size="xs" variant="solid" colorPalette="gray" flexShrink={0}>
          <RouterLink to={UNFINISHED_PAYOUTS_HREF}>
            {t('commissions.unfinishedPayouts.finish')}
          </RouterLink>
        </Button>
      }
    >
      <Text as="span">
        {t('commissions.unfinishedPayouts.body', {
          amount: formatChargeAmount(summary, language),
          count: summary.count,
        })}
      </Text>
      {deadline && (
        <>
          {' '}
          <Text as="span">
            {t('commissions.unfinishedPayouts.deadline', { deadline, count: summary.count })}
          </Text>
        </>
      )}
    </Alert>
  );
};

export default UnfinishedPayoutsNotice;
